import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { Moment } from 'moment';
import moment from 'moment';
import type { Appointment } from 'services/types/appointment';
import { NAVY, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import AppointmentCard from './AppointmentCard';
import { WEEKDAYS_SHORT } from './serviceCalendar.constants';
import { appointmentsOnDay, buildMonthGrid, isSameDay } from './serviceCalendar.utils';

const MAX_VISIBLE = 3;

interface Props {
  anchor: Moment;
  appointments: Appointment[];
  onSelectAppointment: (appointment: Appointment) => void;
  onSelectDay: (day: Moment) => void;
}

const MonthView = ({ anchor, appointments, onSelectAppointment, onSelectDay }: Props) => {
  const days = buildMonthGrid(anchor);
  const today = moment();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      {/* Weekday header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flexShrink: 0 }}>
        {WEEKDAYS_SHORT.map((label) => (
          <Typography
            key={label}
            sx={{
              py: 1,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: 'rgba(21, 24, 58, 0.5)',
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: '1fr',
          flex: 1,
          minHeight: 560,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: SURFACE_CONTAINER_LOWEST,
        }}
      >
        {days.map((day) => {
          const inMonth = day.isSame(anchor, 'month');
          const dayAppointments = appointmentsOnDay(appointments, day);
          const isToday = isSameDay(day, today);
          return (
            <Box
              key={day.toISOString()}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 0.75,
                backgroundColor: inMonth ? SURFACE_CONTAINER_LOWEST : 'rgba(21, 24, 58, 0.02)',
                boxShadow: 'inset 1px 1px 0 rgba(21, 24, 58, 0.04)',
              }}
            >
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: 22,
                  height: 22,
                  px: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: isToday ? YELLOW : 'transparent',
                  fontSize: 12,
                  fontWeight: isToday ? 700 : 500,
                  color: inMonth ? NAVY : 'rgba(21, 24, 58, 0.35)',
                }}
              >
                {day.date()}
              </Box>

              {dayAppointments.slice(0, MAX_VISIBLE).map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  variant="compact"
                  onClick={onSelectAppointment}
                />
              ))}
              {dayAppointments.length > MAX_VISIBLE && (
                <Typography
                  onClick={() => onSelectDay(day)}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: 'rgba(21, 24, 58, 0.5)',
                    pl: 0.5,
                    cursor: 'pointer',
                    '&:hover': { color: NAVY },
                  }}
                >
                  +{dayAppointments.length - MAX_VISIBLE} más
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthView;
